import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { 
  Shield,
  Users,
  Search,
  Plus,
  LogOut,
  User,
  Phone,
  Mail,
  ChevronRight,
  Home,
  ArrowLeft,
  FileText,
  Menu
} from "lucide-react";
import { authGet, getAuthToken, logout } from "../utils/auth";
import MobileBottomNav from "../components/MobileBottomNav";
import MobileSidebar from "../components/MobileSidebar";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdvisorClients() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState(searchParams.get("search") || "");
  const [showSidebar, setShowSidebar] = useState(false);

  useEffect(() => {
    // Check auth via token
    const checkAuth = async () => {
      const token = getAuthToken();
      if (!token) {
        navigate("/advisor/login");
        return;
      }
      try {
        const response = await authGet('/api/auth/me');
        if (!response.ok) {
          navigate("/advisor/login");
          return;
        }
        fetchClients();
      } catch {
        navigate("/advisor/login");
      }
    };
    checkAuth();
  }, [navigate]);

  useEffect(() => {
    const search = searchParams.get("search");
    if (search) {
      setSearchQuery(search);
      fetchClients(search);
    }
  }, [searchParams]);

  const fetchClients = async (search = "") => {
    setLoading(true);
    
    try {
      const url = search 
        ? `/api/advisor/clients?search=${encodeURIComponent(search)}`
        : `/api/advisor/clients`;
        
      const response = await authGet(url);
      
      if (response.status === 401) {
        navigate("/advisor/login");
        return;
      }
      
      setClients(response.data?.clients || []);
    } catch (error) {
      console.error("Error fetching clients:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchClients(searchQuery);
  };

  const handleLogout = () => {
    localStorage.removeItem("advisor_token");
    localStorage.removeItem("advisor_info");
    navigate("/advisor/login");
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFFFFF] to-[#EDF1F8] pb-24 md:pb-0">
      {/* Mobile Sidebar */}
      <MobileSidebar isOpen={showSidebar} onClose={() => setShowSidebar(false)} />

      {/* Mobile Header */}
      <header className="md:hidden bg-white/95 backdrop-blur-xl sticky top-0 z-40 safe-area-inset-top border-b border-[#C9D4E6]">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setShowSidebar(true)}
              className="p-2 -ml-2 rounded-xl bg-[#EDF1F8] hover:bg-[#E2E9F4] active:bg-[#E2E9F4] transition-colors"
            >
              <Menu className="h-5 w-5 text-[#1B2A4A]" />
            </button>
            <div className="h-10 w-10 rounded-xl bg-gradient-to-r from-[#EDF1F8] to-[#DCE4F2] flex items-center justify-center">
              <Users className="h-5 w-5 text-[#0D1A3A]" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-[#1B2A4A]">All Clients</h1>
              <p className="text-xs text-[#3A4557]">{clients.length} clients</p>
            </div>
          </div>
          <button
            onClick={() => navigate("/advisor/clients/new")}
            className="p-2.5 rounded-xl bg-gradient-to-r from-[#D4A017] to-[#D9A82C] active:scale-95 transition-transform"
          >
            <Plus className="h-5 w-5 text-[#0D1A3A]" />
          </button>
        </div>
        
        {/* Mobile Search */}
        <div className="px-4 pb-3">
          <form onSubmit={handleSearch} className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#3A4557]" />
            <Input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search clients..."
              className="pl-10 pr-4 h-10 bg-[#EDF1F8]/50 border-[#C9D4E6] text-[#1B2A4A] placeholder:text-[#3A4557] rounded-xl"
              data-testid="search-clients-input"
            />
          </form>
        </div>
      </header>

      {/* Desktop Header */}
      <header className="hidden md:block bg-[#EDF1F8]/80 backdrop-blur-xl border-b border-[#C9D4E6] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/advisor/dashboard")}
                className="text-[#3A4557] hover:text-[#1B2A4A]"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="h-10 w-10 rounded-xl bg-gradient-to-r from-[#D4A017] to-[#D9A82C] flex items-center justify-center">
                <Users className="h-5 w-5 text-[#0D1A3A]" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-[#1B2A4A]">All Clients</h1>
                <p className="text-[#3A4557] text-sm">{clients.length} clients</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/")}
                className="text-[#3A4557] hover:text-[#1B2A4A]"
              >
                <Home className="h-4 w-4 mr-2" />
                Website
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                className="border-[#C9D4E6] text-[#2E3B57] hover:text-[#1B2A4A] hover:bg-[#EDF1F8]"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-4 md:py-8">
        {/* Desktop Search & Add */}
        <div className="hidden md:flex flex-col sm:flex-row gap-4 mb-8">
          <form onSubmit={handleSearch} className="flex-1 flex gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#3A4557]" />
              <Input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, mobile, or email..."
                className="pl-10 bg-white/50 border-[#C9D4E6] text-[#1B2A4A] placeholder:text-[#3A4557] h-12"
                data-testid="search-clients-input-desktop"
              />
            </div>
            <Button
              type="submit"
              className="bg-[#D4A017] hover:bg-[#D4A017] text-[#0D1A3A] px-6"
              data-testid="search-btn"
            >
              Search
            </Button>
          </form>
          <Button
            onClick={() => navigate("/advisor/clients/new")}
            className="bg-gradient-to-r from-[#D4A017] via-yellow-500 to-[#D9A82C] text-[#0D1A3A] font-bold rounded-full shadow-lg shadow-[#D4A017]/15"
            data-testid="add-client-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add New Client
          </Button>
        </div>

        {/* Clients List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="h-12 w-12 border-4 border-[#D4A017] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-[#3A4557]">Loading clients...</p>
          </div>
        ) : clients.length === 0 ? (
          <div className="bg-[#EDF1F8]/30 border border-[#C9D4E6]/50 rounded-2xl p-8 md:p-12 text-center">
            <Users className="h-16 w-16 mx-auto mb-4 text-[#3A4557]" />
            <h3 className="text-xl font-semibold text-[#1B2A4A] mb-2">No Clients Found</h3>
            <p className="text-[#3A4557] mb-6">
              {searchQuery ? "No clients match your search criteria." : "Start building your client base."}
            </p>
            <Button
              onClick={() => navigate("/advisor/clients/new")}
              className="bg-gradient-to-r from-[#D4A017] via-yellow-500 to-[#D9A82C] text-[#0D1A3A] font-bold"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Your First Client
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {clients.map((client) => (
              <button
                key={client.id}
                onClick={() => navigate(`/advisor/clients/${client.id}`)}
                className="w-full bg-[#EDF1F8]/30 border border-[#C9D4E6]/50 hover:border-[#D4A017]/50 rounded-2xl p-4 transition-all active:scale-[0.98] text-left"
                data-testid={`client-card-${client.id}`}
              >
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-full bg-[#EDF1F8]/40 flex items-center justify-center flex-shrink-0">
                    <User className="h-6 w-6 text-[#2E3B57]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-semibold text-[#1B2A4A] truncate">{client.name}</h3>
                    <div className="flex items-center gap-3 text-sm text-[#3A4557] mt-1">
                      <span className="flex items-center gap-1">
                        <Phone className="h-3.5 w-3.5" />
                        {client.mobile}
                      </span>
                      {client.city && (
                        <span className="text-[#3A4557] hidden sm:inline">{client.city}</span>
                      )}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="flex items-center gap-1.5 text-[#A87608]">
                      <FileText className="h-4 w-4" />
                      <span className="font-bold text-lg">{client.policy_count || 0}</span>
                    </div>
                    <p className="text-[#3A4557] text-xs">policies</p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-[#3A4557] flex-shrink-0 hidden sm:block" />
                </div>
              </button>
            ))}
          </div>
        )}
      </main>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav onMenuClick={() => setShowSidebar(true)} />
    </div>
  );
}
